import React, { useState, useEffect } from "react";
import { SearchableSelect, SearchableSelectOption } from "./SearchableSelect";
import { FolderKanban } from "lucide-react";

interface Project {
  _id: string;
  projectNumber: string;
  name?: string;
}

interface ProjectSelectorProps {
  value: string | null | undefined;
  onChange: (projectId: string | null) => void;
  accountId?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  dropdownZIndex?: number;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  value,
  onChange,
  accountId,
  className = "",
  disabled = false,
  placeholder = "Select a project...",
  dropdownZIndex,
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    fetchProjects();
  }, [accountId]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${apiUrl}/api/projects?isActive=true&limit=1000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data.success) {
        setProjects(data.data || []);
      } else {
        setError(data.message || "Failed to fetch projects");
      }
    } catch (err) {
      console.error("Fetch projects error:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const options: SearchableSelectOption[] = [
    {
      value: "",
      label: "None (Unmapped)",
    },
    ...projects.map((project) => ({
      value: project._id,
      label: `${project.projectNumber}${project.name ? ` - ${project.name}` : ""}`,
    })),
  ];

  const handleChange = (selectedValue: string) => {
    onChange(selectedValue === "" ? null : selectedValue);
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-gray-500 dark:text-gray-400 ${className}`}>
        <FolderKanban className="w-4 h-4 animate-pulse" />
        <span className="text-sm">Loading projects...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm text-red-600 dark:text-red-400 ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <SearchableSelect
      value={value || ""}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      icon={<FolderKanban className="w-4 h-4" />}
      filterFunction={(option, searchTerm) => {
        if (!searchTerm.trim()) return true;
        const searchLower = searchTerm.toLowerCase();
        return option.label.toLowerCase().includes(searchLower);
      }}
      emptyMessage="No projects found"
      dropdownZIndex={dropdownZIndex}
    />
  );
};
